/**
 * The register books and the letters are the parish's (spec 15.5). A ketua (lingkungan or
 * stasi, `adalahKetua`) who reaches `/buku/*` or `/surat` by its address gets the title and
 * one sentence and no request: the worker refuses it anyway (db/queries/hanyaParoki),
 * whatever rows a shared device still holds (spec 15.4). `JagaDataParoki` (plan 10) does the
 * same in front of every parish data screen for a diocese view, and on Mutasi and Impor a ketua.
 * `JagaPeta` (release 20 ruling 2) in front of Peta for the uskup and a Ketua Wilayah.
 */
import {
  adalahKetua,
  bolehDataParoki,
  bolehLihatPeta,
  bolehUbahDataParoki,
  hanyaBacaParoki,
  uiStrings,
} from '@satu/shared'
import type { ComponentType, ReactNode } from 'react'
import { Outlet, useMatches } from 'react-router'
import { crumbsFromMatches } from '@/app/shell/crumbs'
import { useSesi } from '@/app/store/sesi'
import { MonoMark } from '@/components/primitives/MonoMark'
import { TitleRow } from '@/components/primitives/TitleRow'

/** What a refused screen shows in its place: the page's title and one sentence. */
function HalamanDitolak({ judul, kalimat }: { judul: string; kalimat: string }) {
  return (
    <article className="pb-10" aria-label={judul} data-akses="ditolak">
      <TitleRow judul={judul} />
      <div className="flex flex-col items-start gap-2">
        <MonoMark className="size-6 text-text" />
        <p className="measure font-prose text-prose text-muted">{kalimat}</p>
      </div>
    </article>
  )
}

export function HanyaParoki({
  judul,
  kalimat,
  children,
}: {
  judul: string
  kalimat: string
  children: ReactNode
}) {
  const peran = useSesi((s) => s.sesi.peran)
  if (!adalahKetua(peran)) return children
  return <HalamanDitolak judul={judul} kalimat={kalimat} />
}

/** A route component behind `HanyaParoki`, for the route modules' exports. */
export function hanyaParoki(
  Komponen: ComponentType,
  teks: { judul: string; kalimat: string },
): ComponentType {
  function Terjaga() {
    return (
      <HanyaParoki judul={teks.judul} kalimat={teks.kalimat}>
        <Komponen />
      </HanyaParoki>
    )
  }
  Terjaga.displayName = `HanyaParoki(${Komponen.displayName ?? Komponen.name})`
  return Terjaga
}

/**
 * The access ruling of 2026-10-03 (plan 10): the layout route in front of the parish's data
 * screens (app/router). The parish roles and an admin keuskupan working in a parish see the
 * screen; a diocese view reads one sentence under the page's own title (an admin keuskupan the
 * way in: Bekerja di), and with `tanpaKetua` (Mutasi, Impor) a ketua too. The worker refuses
 * the same requests (db/queries/hanyaParoki `jagaDataParoki`).
 *
 * Release 32: with `ubah` (Tambah umat, Pendaftaran, Salin buku, Tambah entri) the screen changes
 * the parish's data, so a Pastor Paroki, who reads them, reads one sentence instead; his worker
 * refuses those writes too (db/handlers).
 */
export function JagaDataParoki({
  tanpaKetua = false,
  ubah = false,
}: {
  tanpaKetua?: boolean
  ubah?: boolean
}) {
  const peran = useSesi((s) => s.sesi.peran)
  const adminKeuskupan = useSesi((s) => s.asli.peran === 'admin_keuskupan')
  const teks = uiStrings.aksesParoki
  const judul = crumbsFromMatches(useMatches()).at(-1)?.label ?? teks.judul
  const boleh = ubah ? bolehUbahDataParoki(peran, tanpaKetua) : bolehDataParoki(peran, tanpaKetua)
  if (boleh) return <Outlet />
  const kalimat = adminKeuskupan
    ? teks.pilihParoki
    : ubah && hanyaBacaParoki(peran)
      ? teks.hanyaBaca
      : teks.kalimat
  return <HalamanDitolak judul={judul} kalimat={kalimat} />
}

/**
 * Peta umat (release 20 ruling 2): the layout route in front of /peta. The parish roles, both
 * ketua and the admin keuskupan see the map; the uskup and a Ketua Wilayah, who reach it only by
 * its address, read the page's title and one sentence, and nothing is asked of the worker (which
 * refuses `peta:keluarga` to them anyway, db/queries/peta).
 */
export function JagaPeta() {
  const peran = useSesi((s) => s.sesi.peran)
  if (bolehLihatPeta(peran)) return <Outlet />
  return <HalamanDitolak judul={uiStrings.peta.judul} kalimat={uiStrings.peta.layar.tanpaAkses} />
}
